import { useCallback, useMemo } from 'react';

import { type QueryOptions } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  type RentalComparable,
  netRentPerSqm,
} from '@realadvisor/listings/rental-cma';
import { ExternalLinkIcon, MapPinIcon } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import type {
  ComparableCmaListingDetailsQuery,
  ComparableCmaListingDetailsQueryVariables,
  PropertyDetailsFragment,
} from '@graphql/__generated__/graphql';
import { CollapsibleContent } from '@modules/_shared/components/CollapsibleContent';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import MarkdownParser from '@modules/_shared/components/MarkdownParser';
import { PropertyDetails } from '@modules/_shared/components/PropertyDetails';
import {
  PropertyFeatures,
  type TPropertyFeatures,
} from '@modules/_shared/components/PropertyFeatures';
import { useNavigateWithSearchParams } from '@modules/_shared/hooks/useNavigateWithSearchParams';
import { usePropertyTypes } from '@modules/_shared/providers/PropertyTypesProvider';
import { ImagesCarousel } from '@modules/listings/ListingOverview';
import { COMPARABLE_CMA_LISTING_DETAILS } from '@modules/reports/cma-reports/cmaReportsQueries';
import { RENTAL_CMA_LISTINGS } from '@modules/reports/cma-reports/rentalQueries';
import ListingStatusChip from '@modules/reports/cma-reports/step-listing-compare/ListingStatusChip';
import {
  getRentalCountryRows,
  getRentalCountryVariables,
} from '@modules/reports/cma-reports/step-listing-compare/rentalComparablesSearch';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

const truncateUrl = (url: string) => {
  const parsedUrl = new URL(url);
  return `${parsedUrl.protocol}//${parsedUrl.hostname}`;
};

type ComparableCmaListingDetails =
  | ComparableCmaListingDetailsQuery['ch_cma_listings_by_pk']
  | ComparableCmaListingDetailsQuery['fr_cma_listings_by_pk']
  | ComparableCmaListingDetailsQuery['es_cma_listings_by_pk']
  | ComparableCmaListingDetailsQuery['it_cma_listings_by_pk']
  | ComparableCmaListingDetailsQuery['pl_cma_listings_by_pk']
  | ComparableCmaListingDetailsQuery['pt_cma_listings_by_pk'];

export type ScrappedListing = NonNullable<ComparableCmaListingDetails>;

export const initialListingComparableDetails = (
  listingId: number | string,
  countryCode: string,
  stateId: string,
): QueryOptions<ComparableCmaListingDetailsQueryVariables> => {
  const id = typeof listingId === 'string' ? parseInt(listingId) : listingId;

  return {
    query: COMPARABLE_CMA_LISTING_DETAILS,
    variables: {
      id,
      stateId,
      isCh: countryCode === 'CH',
      isEs: countryCode === 'ES',
      isFr: countryCode === 'FR',
      isIt: countryCode === 'IT',
      isPl: countryCode === 'PL',
      isPt: countryCode === 'PT',
    },
    context: { clientName: 'scrapers' },
  };
};

type ListingComparableDetailsProps = {
  // When provided, the drawer renders for this scraper listing directly instead
  // of decoding the `:encodedListingId` route param, and `onClose` is called on
  // close instead of navigating up. Used by callers that drive the drawer from
  // local state (e.g. the listing Activity tab comparable carousels).
  listingId?: number | string;
  stateId?: string;
  onClose?: () => void;
  rentalComparable?: RentalComparable;
};

const ListingComparableDetails = ({
  listingId,
  stateId: stateIdProp,
  onClose,
  rentalComparable,
}: ListingComparableDetailsProps = {}) => {
  const { t, countryCode, locale } = useLocale();
  const theme = useTheme();
  const navigate = useNavigateWithSearchParams();
  const currencyToFormat = getCurrencyByCountryCode(countryCode);
  const { encodedListingId } = useParams();
  const { getPropertyTypeLabel } = usePropertyTypes();

  const { scrapperListingId, stateId } = useMemo(() => {
    if (listingId != null) {
      return {
        scrapperListingId: String(listingId),
        stateId: stateIdProp ?? '',
      };
    }
    if (!encodedListingId) {
      return { scrapperListingId: '', stateId: '' };
    }
    const decoded = atob(encodedListingId);
    const [decodedListingId, decodedStateId] = decoded.split('/');
    return { scrapperListingId: decodedListingId, stateId: decodedStateId };
  }, [listingId, stateIdProp, encodedListingId]);

  const { variables } = useMemo(
    () =>
      initialListingComparableDetails(scrapperListingId, countryCode, stateId),
    [scrapperListingId, countryCode, stateId],
  );

  const {
    data,
    error: saleError,
    loading: saleLoading,
  } = useQuery(COMPARABLE_CMA_LISTING_DETAILS, {
    skip: rentalComparable != null,
    variables,
    context: { clientName: 'scrapers' },
  });

  const {
    data: rentalData,
    error: rentalError,
    loading: rentalLoading,
  } = useQuery(RENTAL_CMA_LISTINGS, {
    skip: rentalComparable == null,
    variables: {
      ...getRentalCountryVariables(countryCode, {
        id: { _eq: rentalComparable?.id ?? 0 },
        offer_type: { _eq: 'rent' },
      }),
      limit: 1,
    },
    context: { clientName: 'scrapers' },
  });
  const error = rentalComparable ? rentalError : saleError;
  const loading = rentalComparable ? rentalLoading : saleLoading;

  const handleClose = useCallback(() => {
    if (onClose != null) {
      onClose();
      return;
    }
    navigate('..');
  }, [onClose, navigate]);

  const scrappedListing = useMemo<Partial<ScrappedListing> | null>(
    () =>
      rentalComparable
        ? {
            ...getRentalCountryRows(rentalData)[0],
            id: rentalComparable.id,
            address: rentalComparable.address,
            living_surface: rentalComparable.surface,
            number_of_rooms: rentalComparable.rooms,
          }
        : data?.ch_cma_listings_by_pk ??
          data?.fr_cma_listings_by_pk ??
          data?.es_cma_listings_by_pk ??
          data?.it_cma_listings_by_pk ??
          data?.pl_cma_listings_by_pk ??
          data?.pt_cma_listings_by_pk ??
          null,
    [data, rentalData, rentalComparable],
  );
  const propertyLabel = getPropertyTypeLabel(
    scrappedListing?.property_type ?? '',
  );

  const scrappedProperty = useMemo<
    TPropertyFeatures & PropertyDetailsFragment
  >(() => {
    return {
      id: String(scrappedListing?.id ?? ''),
      created_at: scrappedListing?.valid_from,
      is_oriented_north: false,
      is_oriented_south: false,
      is_oriented_east: false,
      is_oriented_west: false,
      property_type: {
        id: String(scrappedListing?.id),
        label: propertyLabel,
        main_type: scrappedListing?.property_main_type,
      },
      // Compatibility with PropertyFeatures component
      __property_type: {
        name: scrappedListing?.property_type,
        main_type: scrappedListing?.property_main_type,
      },
      property_images: [],
      living_surface: scrappedListing?.living_surface,
      land_surface: scrappedListing?.land_surface,
      floor_of_flat: scrappedListing?.floor_of_flat,
      construction_year: scrappedListing?.construction_year,
      number_of_rooms: scrappedListing?.number_of_rooms,
      number_of_bedrooms: scrappedListing?.number_of_bedrooms,
      number_of_bathrooms: scrappedListing?.number_of_bathrooms,
      number_of_toilets: scrappedListing?.number_of_toilets,
      number_of_indoor_parkings: scrappedListing?.number_of_indoor_parkings,
      number_of_outdoor_parkings: scrappedListing?.number_of_outdoor_parkings,
      has_view: scrappedListing?.has_view,
      has_cellar: scrappedListing?.has_cellar,
      has_elevator: scrappedListing?.has_elevator,
      has_fireplace: scrappedListing?.has_fireplace,
      has_storage_room: scrappedListing?.has_storage_room,
      has_swimming_pool: scrappedListing?.has_swimming_pool,
      is_child_friendly: scrappedListing?.is_child_friendly,
      is_wheelchair_accessible: scrappedListing?.is_wheelchair_accessible,
      is_new_development: scrappedListing?.is_new_development ?? false,
    };
  }, [propertyLabel, scrappedListing]);

  return (
    <Drawer
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: ['100vw', '100vw', '100vw', `calc(100vw - 550px)`, '50vw'],
          },
        },
      }}
      sx={{
        zIndex: 2000,
      }}
      title={
        <Box>
          <Typography variant="h6">{t('Listing details')}</Typography>
        </Box>
      }
    >
      {error != null ? (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      ) : loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : scrappedListing == null ? (
        <Alert severity="warning" sx={{ m: 2 }}>
          {t('Listing not found')}
        </Alert>
      ) : (
        scrappedListing && (
          <Paper elevation={0} sx={{ flexGrow: 1 }}>
            <ImagesCarousel
              listing={{
                created_at: scrappedListing.created_at,
                images: scrappedListing.images ?? [],
              }}
            />
            <Box
              sx={{
                p: 3,
              }}
            >
              <Grid container spacing={3}>
                <Grid size={12}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontWeight: 'bold',
                    }}
                  >
                    {scrappedListing.title || propertyLabel}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      mb: 1,
                      alignItems: 'center',
                    }}
                  >
                    <MapPinIcon size={18} />
                    <Typography variant="body1">
                      {scrappedListing.address}
                    </Typography>
                  </Stack>

                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                      }}
                    >
                      {formatPrice(
                        rentalComparable?.netMonthly ??
                          scrappedListing.sale_price ??
                          0,
                        locale,
                        currencyToFormat,
                      )}
                      {rentalComparable && ` ${t('per month short')}`}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {formatPrice(
                        rentalComparable
                          ? netRentPerSqm(rentalComparable)
                          : scrappedListing.computed_price_per_sqm ?? 0,
                        locale,
                        currencyToFormat,
                      )}{' '}
                      / m²
                    </Typography>
                    {rentalComparable?.chargesMonthly != null &&
                      rentalComparable.chargesMonthly > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {t('charges')} ({t('per month short')}):{' '}
                          {formatPrice(
                            rentalComparable.chargesMonthly,
                            locale,
                            currencyToFormat,
                          )}
                        </Typography>
                      )}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        mt: 1,
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <ListingStatusChip
                        listing={scrappedListing}
                        variant="duration"
                      />
                      <ListingStatusChip
                        listing={scrappedListing}
                        variant="status"
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                    {t('Description')}
                  </Typography>
                  {scrappedListing.description &&
                  scrappedListing.description.length > 0 ? (
                    <CollapsibleContent nbLinesToShow={6}>
                      <Box
                        sx={{
                          typography: 'body2',
                          '& p, ol, ul': {
                            marginTop: 0,
                            marginBottom: '1em',
                          },
                        }}
                      >
                        <MarkdownParser
                          markdown={scrappedListing.description
                            .replace(/(<br>\s*)*$/g, '')
                            .trim()}
                        />
                      </Box>
                    </CollapsibleContent>
                  ) : (
                    t('No description')
                  )}
                </Grid>
                <Grid size={12}>
                  <Stack
                    spacing={2}
                    sx={{
                      flexDirection: 'column',
                    }}
                  >
                    <PropertyDetails property={scrappedProperty} />
                    <PropertyFeatures property={scrappedProperty} />
                  </Stack>
                </Grid>
                <Grid size={12}>
                  <Grid container>
                    {(!!scrappedListing.agency_name ||
                      !!scrappedListing.agency_contact_address ||
                      !!scrappedListing.agency_contact_person) && (
                      <Grid
                        size={{
                          xs: 6,
                          md: 5,
                        }}
                      >
                        <Stack
                          sx={{
                            gap: 2,
                          }}
                        >
                          {(!!scrappedListing.agency_name ||
                            !!scrappedListing.agency_contact_address) && (
                            <Box>
                              <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                  fontWeight: 'bold',
                                }}
                              >
                                {t('Agency')}
                              </Typography>
                              {scrappedListing.agency_name != null && (
                                <Typography variant="body1">
                                  {scrappedListing.agency_name}
                                </Typography>
                              )}
                              {scrappedListing.agency_contact_address !=
                                null && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'text.secondary',
                                  }}
                                >
                                  {scrappedListing.agency_contact_address}
                                </Typography>
                              )}
                            </Box>
                          )}
                          {scrappedListing.agency_contact_person != null && (
                            <Box>
                              <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                  fontWeight: 'bold',
                                }}
                              >
                                {t('Agency contact')}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.secondary',
                                  mt: 1,
                                }}
                              >
                                {scrappedListing.agency_contact_person}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Grid>
                    )}

                    {scrappedListing.url && (
                      <Grid
                        size={{
                          xs: 6,
                          md: 4,
                        }}
                      >
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                            fontWeight: 'bold',
                          }}
                        >
                          {t('Found on')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            mt: 1,
                            fontWeight: 'medium',
                            display: 'flex',
                            alignItems: 'center',

                            '&:hover': {
                              textDecoration:
                                scrappedListing.offline_since == null
                                  ? 'underline'
                                  : 'none',
                            },
                          }}
                        >
                          {scrappedListing.offline_since == null ? (
                            <Link
                              to={scrappedListing.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: (theme.vars ?? theme).palette.primary
                                  .main,
                                display: 'flex',
                                alignItems: 'center',
                                textDecoration: 'none',
                              }}
                            >
                              {truncateUrl(scrappedListing.url)}
                              <ExternalLinkIcon
                                style={{ marginLeft: 4 }}
                                size={'1em'}
                              />
                            </Link>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: (theme.vars ?? theme).palette.text
                                  .disabled,
                              }}
                            >
                              {truncateUrl(scrappedListing.url)}
                              <ExternalLinkIcon
                                style={{ marginLeft: 4 }}
                                size={'1em'}
                              />
                            </Box>
                          )}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        )
      )}
    </Drawer>
  );
};

export default ListingComparableDetails;
